import isEmpty from "lodash-es/isEmpty";
import { AirStatusesModel } from "./airCard.types";
import { SeaStatusesModel } from "./seaCard.types";
import { CardDates, ShipmentType } from "./shipmentCard.types";
import { DateTimeModel } from "./shipmentCard.types";
import { TO_BE_NOMINATED } from "shared/constants/common.constants";
import { CompatibleString, Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { t } from "shared/localization/localization";
import { formatCardDate, FormatCardDateOptions, getUtcTimestamp } from "shared/utils/dateTime.utils";

export const resolveDateValues = ({
  formatedDate = false,
  dateFormatOptions = {},
  useValuePlaceholder = false,
  atd,
  etd,
  ata,
  eta,
  predictiveETA,
}: {
  atd: Nullable<DateTimeModel>;
  etd: Nullable<DateTimeModel>;
  ata: Nullable<DateTimeModel>;
  eta: Nullable<DateTimeModel>;
  predictiveETA?: Nullable<DateTimeModel>;
  formatedDate?: boolean;
  dateFormatOptions?: FormatCardDateOptions;
  useValuePlaceholder?: boolean;
}): {
  departureDate: {
    value: string | null;
    unit: string;
    isActual: boolean;
  };
  arrivalDate: {
    value: string | null;
    unit: string;
    isActual: boolean;
    isPredictive: boolean;
  };
} => {
  const departureDate = atd ?? etd;
  const arrivalDate = ata ?? eta ?? predictiveETA;

  const departureDateValue =
    departureDate && !isEmpty(departureDate)
      ? formatedDate
        ? formatCardDate(departureDate, { withTime: false, format: "24h", ...dateFormatOptions })
        : departureDate
      : null;

  const arrivalDateValue =
    arrivalDate && !isEmpty(arrivalDate)
      ? formatedDate
        ? formatCardDate(arrivalDate, { withTime: false, format: "24h", ...dateFormatOptions })
        : arrivalDate
      : null;

  const isActualArrivalDate = !!ata;
  const isEta = !!eta;
  const isPredictiveArrivalDate = !isActualArrivalDate && !isEta && !!predictiveETA;

  return {
    departureDate: {
      value: departureDateValue ?? (useValuePlaceholder ? TO_BE_NOMINATED : null),
      unit: atd ? CardDates.atd.toUpperCase() : CardDates.etd.toUpperCase(),
      isActual: !!atd,
    },
    arrivalDate: {
      value: arrivalDateValue ?? (useValuePlaceholder ? TO_BE_NOMINATED : null),
      unit: ata
        ? CardDates.ata.toUpperCase()
        : isPredictiveArrivalDate
          ? `${CardDates.eta.toUpperCase()} (${t("predictive")})`
          : CardDates.eta.toUpperCase(),
      isActual: isActualArrivalDate,
      isPredictive: isPredictiveArrivalDate,
    },
  };
};

export const calcProgress = ({
  startDate,
  endDate,
  airStatus,
  seaStatus,
}: {
  startDate: Nullable<DateTimeModel>;
  endDate: Nullable<DateTimeModel>;
  airStatus?: ValueOf<typeof AirStatusesModel>;
  seaStatus?: ValueOf<typeof SeaStatusesModel>;
}): number => {
  if (airStatus) {
    const notStartedStatusesSet = new Set([
      AirStatusesModel.BOOKED,
      AirStatusesModel.RECEIVED,
      AirStatusesModel.PLANNED,
    ]);
    const completedStatusesSet = new Set([
      AirStatusesModel.NOTIFIED,
      AirStatusesModel.DELIVERED,
      AirStatusesModel.ARRIVED,
    ]);
    if (notStartedStatusesSet.has(airStatus)) return 0;
    if (completedStatusesSet.has(airStatus)) return 100;
  }

  if (seaStatus === SeaStatusesModel.PLANNED) return 0;

  if (!startDate || !endDate || startDate === endDate) return 0;

  const start: number = getUtcTimestamp(startDate);
  const end: number = getUtcTimestamp(endDate);
  const today: number = getUtcTimestamp(new Date());

  const totalTime = Math.abs(end - start);
  const elapsedTime = Math.abs(today - start);

  let percentageElapsed: number;

  if (elapsedTime <= 0) {
    percentageElapsed = 0;
  } else if (elapsedTime >= totalTime) {
    percentageElapsed = 100;
  } else {
    percentageElapsed = Math.round((elapsedTime / totalTime) * 100);
  }

  return percentageElapsed;
};

export class ShipmentTypeChecker {
  static isSea(shipmentType: ValueOf<typeof ShipmentType>) {
    return shipmentType === ShipmentType.SEA;
  }

  static isAir(shipmentType: ValueOf<typeof ShipmentType>) {
    return shipmentType === ShipmentType.AIR;
  }

  static isRail(shipmentType: ValueOf<typeof ShipmentType>) {
    return shipmentType === ShipmentType.RAIL;
  }

  static isRoad(shipmentType: ValueOf<typeof ShipmentType>) {
    return shipmentType === ShipmentType.ROAD;
  }
  static isParcel(shipmentType: ValueOf<typeof ShipmentType>) {
    return shipmentType === ShipmentType.PARCEL;
  }
}

export const isSeaStatusModel = (
  status: CompatibleString<ValueOf<typeof SeaStatusesModel>>
): status is ValueOf<typeof SeaStatusesModel> => {
  return Object.values(SeaStatusesModel).includes(status);
};

export const isAirStatusModel = (
  status: CompatibleString<ValueOf<typeof AirStatusesModel>>
): status is ValueOf<typeof AirStatusesModel> => {
  return Object.values(AirStatusesModel).includes(status);
};
